import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import api, { resolveAssetUrl } from '../api';
import OfferDeskNav from '../components/OfferDeskNav';

/* ─── Data ───────────────────────────────────────────────── */
const SECTORS = [
  { id: 'investment_banking', label: 'Investment Banking' },
  { id: 'private_equity',     label: 'Private Equity'     },
  { id: 'venture_capital',    label: 'Venture Capital'    },
  { id: 'consulting',         label: 'Consulting'         },
  { id: 'asset_management',   label: 'Asset Management'   },
  { id: 'hedge_fund',         label: 'Hedge Fund'         },
  { id: 'fintech',            label: 'Fintech'            },
  { id: 'corporate_finance',  label: 'Corporate Finance'  },
  { id: 'accounting',         label: 'Accounting'         },
];

const CITIES = ['London', 'Amsterdam', 'Paris', 'Frankfurt', 'Madrid', 'Milan', 'Barcelona', 'Zurich', 'Warsaw', 'Brussels', 'Stockholm', 'Belfast', 'Kyiv'];

/* ─── Icons ──────────────────────────────────────────────── */
const Ic = {
  check:    (c='#065F46') => <svg width="11" height="11" fill="none" stroke={c} viewBox="0 0 24 24" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>,
  upload:   () => <svg width="22" height="22" fill="none" stroke="#4E6C7E" viewBox="0 0 24 24" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg>,
  pdf:      () => <svg width="26" height="26" fill="none" stroke="#4E6C7E" viewBox="0 0 24 24" strokeWidth={1.6}><path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg>,
  linkedin: () => <svg width="13" height="13" fill="#0A66C2" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>,
  globe:    () => <svg width="16" height="16" fill="none" stroke="#4E6C7E" viewBox="0 0 24 24" strokeWidth={1.8}><circle cx="12" cy="12" r="9"/><path strokeLinecap="round" strokeLinejoin="round" d="M3.6 9h16.8M3.6 15h16.8M12 3a14.5 14.5 0 010 18M12 3a14.5 14.5 0 000 18"/></svg>,
  pin:      () => <svg width="13" height="13" fill="none" stroke="#64748B" viewBox="0 0 24 24" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>,
  save:     () => <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>,
  user:     () => <svg width="18" height="18" fill="none" stroke="rgba(255,255,255,0.6)" viewBox="0 0 24 24" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  target:   () => <svg width="16" height="16" fill="none" stroke="#4E6C7E" viewBox="0 0 24 24" strokeWidth={1.8}><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
  location: () => <svg width="16" height="16" fill="none" stroke="#4E6C7E" viewBox="0 0 24 24" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>,
  info:     () => <svg width="14" height="14" fill="none" stroke="#4E6C7E" viewBox="0 0 24 24" strokeWidth={2}><circle cx="12" cy="12" r="10"/><path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-4M12 8h.01"/></svg>,
};

/* ─── FadeIn ─────────────────────────────────────────────── */
function FadeIn({ children, delay = 0, y = 20 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  );
}

/* ─── Glass Card ─────────────────────────────────────────── */
function Card({ children, style, delay = 0 }) {
  return (
    <FadeIn delay={delay}>
      <div style={{
        background: 'rgba(255,255,255,0.78)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.95)',
        borderRadius: 20,
        padding: '24px',
        boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
        ...style,
      }}>
        {children}
      </div>
    </FadeIn>
  );
}

/* ─── Animated Progress Bar ──────────────────────────────── */
function ProgressBar({ pct }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  return (
    <div ref={ref} style={{ height: 6, borderRadius: 100, background: 'rgba(255,255,255,0.15)', overflow: 'hidden', marginTop: 8 }}>
      <motion.div
        initial={{ width: 0 }}
        animate={inView ? { width: `${pct}%` } : {}}
        transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        style={{
          height: '100%', borderRadius: 100,
          background: pct === 100
            ? 'linear-gradient(90deg,#10B981,#34D399)'
            : 'var(--tandm-gradient-cta)',
        }}
      />
    </div>
  );
}

/* ─── Toast ──────────────────────────────────────────────── */
function Toast({ toast }) {
  return (
    <AnimatePresence>
      {toast && (
        <motion.div
          initial={{ opacity: 0, y: 16, x: '-50%' }}
          animate={{ opacity: 1, y: 0, x: '-50%' }}
          exit={{ opacity: 0, y: 8, x: '-50%' }}
          style={{
            position: 'fixed', bottom: 28, left: '50%',
            padding: '11px 20px', borderRadius: 100,
            fontSize: 13, fontWeight: 500, zIndex: 999,
            whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 8,
            boxShadow: '0 8px 28px rgba(0,0,0,0.15)',
            background: toast.type === 'error' ? '#FFF0F0' : '#0B1220',
            color: toast.type === 'error' ? '#D94040' : '#fff',
            border: toast.type === 'error' ? '1px solid #FECACA' : '1px solid rgba(255,255,255,0.1)',
            fontFamily: 'var(--tandm-font-sans)',
          }}
        >
          {toast.type === 'error'
            ? <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><circle cx="12" cy="12" r="10"/><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01"/></svg>
            : <svg width="13" height="13" fill="none" stroke="#4E6C7E" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
          }
          {toast.msg}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ─── Main Profile ───────────────────────────────────────── */
export default function Profile() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    first_name: '', last_name: '', university: '',
    graduation_year: '', graduation_month: '', degree: '', linkedin_url: '',
    years_experience: '',
    city: [], nationality: '', phone: '', current_location: '',
    postal_code: '', country: '', work_authorization: '',
    gender: '', portfolio_url: '', website_url: '', requires_sponsorship: false,
  });
  const [targetSectors, setTargetSectors] = useState([]);
  const [isInternational, setIsInternational] = useState(false);
  const [cvUrl, setCvUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    api.get('/profile').then(res => {
      const u = res.data.user;
      setFormData({
        first_name: u.first_name || '', last_name: u.last_name || '',
        university: u.university || '', graduation_year: u.graduation_year || '',
        graduation_month: u.graduation_month || '', degree: u.degree || '', linkedin_url: u.linkedin_url || '',
        years_experience: u.years_experience || '',
        city: u.city ? (Array.isArray(u.city) ? u.city : [u.city]) : [],
        nationality: u.nationality || '',
        phone: u.phone || '',
        current_location: u.current_location || '',
        postal_code: u.postal_code || '',
        country: u.country || '',
        work_authorization: u.work_authorization || '',
        gender: u.gender || '',
        portfolio_url: u.portfolio_url || '',
        website_url: u.website_url || '',
        requires_sponsorship: Boolean(u.requires_sponsorship),
      });
      setCvUrl(u.cv_url);
      setTargetSectors(u.target_sectors || []);
      setIsInternational(u.is_international || false);
    }).catch(() => { setToast({ msg: 'Failed to load profile', type: 'error' }); setTimeout(() => setToast(null), 3000); });
  }, []);

  const handleChange = e => setFormData({ ...formData, [e.target.name]: e.target.value });

  const toggleSector = id => setTargetSectors(prev =>
    prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
  );

  const toggleCity = c => {
    const current = Array.isArray(formData.city) ? formData.city : [];
    setFormData({ ...formData, city: current.includes(c) ? current.filter(x => x !== c) : [...current, c] });
  };

  const handleSave = async e => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...formData, target_sectors: targetSectors, is_international: isInternational };
      const res = await api.put('/profile', payload);
      login(localStorage.getItem('tandm_token'), res.data.user);
      showToast('Profile saved — AI scores updated');
    } catch { showToast('Failed to save', 'error'); }
    finally { setSaving(false); }
  };

  const handleCVUpload = async e => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { showToast('Please upload a PDF', 'error'); return; }
    setUploading(true);
    const fd = new FormData(); fd.append('cv', file);
    try {
      const res = await api.post('/profile/cv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCvUrl(res.data.cv_url);
      showToast('CV uploaded — AI features unlocked');
    } catch { showToast('Upload failed', 'error'); }
    finally { setUploading(false); }
  };

  const handleDeleteCV = async () => {
    if (!window.confirm('Remove your CV?')) return;
    try { await api.delete('/profile/cv'); setCvUrl(null); showToast('CV removed'); }
    catch { showToast('Failed to remove CV', 'error'); }
  };

  const checkItems = [
    { label: 'First & last name', done: !!(formData.first_name && formData.last_name) },
    { label: 'University',         done: !!formData.university },
    { label: 'Degree',             done: !!formData.degree },
    { label: 'Graduation year',    done: !!formData.graduation_year },
    { label: 'Target sectors',     done: targetSectors.length > 0 },
    { label: 'City',               done: Array.isArray(formData.city) ? formData.city.length > 0 : !!formData.city },
    { label: 'LinkedIn URL',       done: !!formData.linkedin_url },
    { label: 'Phone number',       done: !!formData.phone },
    { label: 'Current location',   done: !!formData.current_location },
    { label: 'Work authorization', done: !!formData.work_authorization },
    { label: 'CV uploaded',        done: !!cvUrl },
  ];
  const pct = Math.round((checkItems.filter(c => c.done).length / checkItems.length) * 100);
  const cities = Array.isArray(formData.city) ? formData.city : [];

  return (
    <div style={{ minHeight: '100vh', background: '#F4F6F7', fontFamily: 'var(--tandm-font-sans)' }}>
      <style>{`
        /* fonts via index.css tokens */
        @keyframes spin { to { transform: rotate(360deg) } }
        @keyframes gradientShift { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-thumb { background: rgba(78,108,126,0.35); border-radius: 3px; }
        input::placeholder { color: #94A3B8; }
        input:focus { outline: none; border-color: #4E6C7E !important; box-shadow: 0 0 0 3px rgba(78,108,126,0.08); }
        textarea:focus { outline: none; }
        @media (max-width: 768px) { .pf-grid { grid-template-columns: 1fr !important; } .pf-sidebar { position: static !important; } }
      `}</style>

      <OfferDeskNav />

      {/* ── HERO ─────────────────────────────────────────── */}
      <div style={{
        background: 'var(--tandm-gradient-hero-dark)',
        paddingTop: 'var(--tandm-nav-clearance, 112px)',
        paddingLeft: 24, paddingRight: 24, paddingBottom: 52,
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.04, pointerEvents: 'none',
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.5) 1px,transparent 1px)',
          backgroundSize: '36px 36px',
        }} />
        <motion.div
          animate={{ scale: [1, 1.12, 1], opacity: [0.08, 0.14, 0.08] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
          style={{ position: 'absolute', width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle,#4E6C7E 0%,transparent 70%)', right: -40, top: -80, pointerEvents: 'none' }}
        />
        <motion.div
          animate={{ scale: [1, 1.08, 1], opacity: [0.06, 0.1, 0.06] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 3 }}
          style={{ position: 'absolute', width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle,#6366F1 0%,transparent 70%)', left: '20%', bottom: -60, pointerEvents: 'none' }}
        />

        <div style={{ maxWidth: 860, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
              {/* Avatar */}
              <div style={{
                width: 64, height: 64, borderRadius: '50%', flexShrink: 0,
                background: 'var(--tandm-gradient-cta)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 24, fontWeight: 800, color: '#fff',
                boxShadow: '0 0 0 3px rgba(78,108,126,0.3)',
              }}>
                {formData.first_name?.charAt(0).toUpperCase() || <Ic.user />}
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', margin: '0 0 4px' }}>Your profile</p>
                <h1 style={{ fontFamily: 'var(--tandm-font-display)', fontSize: 'clamp(22px,3vw,32px)', fontWeight: 600, color: '#fff', margin: 0, letterSpacing: '-0.03em', lineHeight: 1.2 }}>
                  {formData.first_name ? `${formData.first_name} ${formData.last_name}` : 'Complete your profile'}
                </h1>
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', margin: '4px 0 0' }}>
                  The more you fill in, the smarter Tandm's AI becomes
                </p>
              </div>
              {/* Progress in hero */}
              <div style={{ flexShrink: 0, textAlign: 'right' }}>
                <div style={{ fontSize: 32, fontWeight: 900, color: pct === 100 ? '#34D399' : '#4E6C7E', letterSpacing: '-0.04em', lineHeight: 1 }}>{pct}%</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>{pct === 100 ? 'Complete ✦' : 'complete'}</div>
                <ProgressBar pct={pct} />
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '28px 24px 72px' }}>
        <div className="pf-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: 16, alignItems: 'start' }}>

          {/* ── LEFT ─────────────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* CV Upload */}
            <Card delay={0}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <div style={{ width: 32, height: 32, borderRadius: 9, background: '#FFF0E8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Ic.pdf />
                </div>
                <div>
                  <h2 style={{ fontSize: 14, fontWeight: 700, color: '#0B1220', margin: 0, letterSpacing: '-0.02em' }}>Your CV</h2>
                  <p style={{ fontSize: 11, color: '#94A3B8', margin: 0 }}>PDF only · max 5MB · powers all AI features</p>
                </div>
              </div>

              <AnimatePresence mode="wait">
                {cvUrl ? (
                  <motion.div
                    key="has-cv"
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    style={{ marginTop: 16, background: 'linear-gradient(135deg,#E8F1F3,#FFF0E8)', border: '1px solid #F0D5C4', borderRadius: 14, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 13 }}
                  >
                    <div style={{ width: 42, height: 42, borderRadius: 11, background: '#FFF0E8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Ic.pdf />
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: 13, fontWeight: 600, color: '#0B1220', margin: '0 0 3px' }}>CV uploaded ✦</p>
                      <a href={resolveAssetUrl(cvUrl)} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#4E6C7E', textDecoration: 'none', fontWeight: 500 }}>View PDF →</a>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={() => fileInputRef.current.click()} style={{ fontSize: 12, color: '#475569', background: 'none', border: '1px solid #D9E2E6', borderRadius: 8, padding: '5px 11px', cursor: 'pointer', fontFamily: 'var(--tandm-font-sans)', fontWeight: 500 }}>Replace</motion.button>
                      <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={handleDeleteCV} style={{ fontSize: 12, color: '#94A3B8', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--tandm-font-sans)' }}>Remove</motion.button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.button
                    key="no-cv"
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    whileHover={{ borderColor: '#4E6C7E', background: '#E8F1F3' }}
                    onClick={() => fileInputRef.current.click()}
                    disabled={uploading}
                    style={{
                      width: '100%', marginTop: 16, border: '2px dashed #D9E2E6', borderRadius: 14,
                      padding: '28px 24px', textAlign: 'center', cursor: 'pointer',
                      background: '#FAFAF8', display: 'flex', flexDirection: 'column',
                      alignItems: 'center', gap: 10, fontFamily: 'var(--tandm-font-sans)',
                      transition: 'all 0.2s',
                    }}
                  >
                    {uploading ? (
                      <div style={{ width: 32, height: 32, border: '3px solid #D9E2E6', borderTopColor: '#4E6C7E', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                    ) : (
                      <div style={{ width: 46, height: 46, borderRadius: 13, background: '#FFF0E8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Ic.upload />
                      </div>
                    )}
                    <div>
                      <p style={{ fontSize: 14, fontWeight: 600, color: '#0B1220', margin: '0 0 3px' }}>{uploading ? 'Uploading...' : 'Upload your CV'}</p>
                      <p style={{ fontSize: 12, color: '#94A3B8', margin: 0 }}>Click to browse · PDF only · max 5MB</p>
                    </div>
                  </motion.button>
                )}
              </AnimatePresence>
              <input type="file" ref={fileInputRef} onChange={handleCVUpload} accept=".pdf" style={{ display: 'none' }} />
            </Card>

            {/* Target Sectors */}
            <Card delay={0.08}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{ width: 32, height: 32, borderRadius: 9, background: '#FFF0E8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Ic.target />
                </div>
                <div>
                  <h2 style={{ fontSize: 14, fontWeight: 700, color: '#0B1220', margin: 0, letterSpacing: '-0.02em' }}>Target sectors</h2>
                  <p style={{ fontSize: 11, color: '#94A3B8', margin: 0 }}>Tailors your match scores and cover letters</p>
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {SECTORS.map((s, i) => {
                  const active = targetSectors.includes(s.id);
                  return (
                    <motion.button
                      key={s.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.03 }}
                      whileHover={{ y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => toggleSector(s.id)}
                      style={{
                        padding: '7px 14px', borderRadius: 100, cursor: 'pointer',
                        fontSize: 12, fontWeight: 500, fontFamily: 'var(--tandm-font-sans)',
                        border: active ? 'none' : '1.5px solid #D9E2E6',
                        background: active ? 'var(--tandm-gradient-cta)' : 'rgba(255,255,255,0.8)',
                        color: active ? '#fff' : '#475569',
                        boxShadow: active ? '0 2px 8px rgba(78,108,126,0.3)' : 'none',
                        backdropFilter: 'blur(8px)',
                        transition: 'all 0.15s',
                        display: 'flex', alignItems: 'center', gap: 5,
                      }}
                    >
                      {active && <Ic.check c="#fff" />}
                      {s.label}
                    </motion.button>
                  );
                })}
              </div>
              <AnimatePresence>
                {targetSectors.length > 0 && (
                  <motion.p
                    initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                    style={{ fontSize: 11, color: '#4E6C7E', margin: '12px 0 0', fontWeight: 500 }}
                  >
                    {targetSectors.length} sector{targetSectors.length > 1 ? 's' : ''} selected — match scores will prioritise these
                  </motion.p>
                )}
              </AnimatePresence>
            </Card>

            {/* Location & Eligibility */}
            <Card delay={0.12}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                <div style={{ width: 32, height: 32, borderRadius: 9, background: '#FFF0E8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Ic.location />
                </div>
                <div>
                  <h2 style={{ fontSize: 14, fontWeight: 700, color: '#0B1220', margin: 0, letterSpacing: '-0.02em' }}>Location & eligibility</h2>
                  <p style={{ fontSize: 11, color: '#94A3B8', margin: 0 }}>Surfaces roles you're eligible for</p>
                </div>
              </div>

              {/* City multi-select */}
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Target cities</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 10 }}>
                  {CITIES.map(c => {
                    const active = cities.includes(c);
                    return (
                      <motion.button
                        key={c}
                        whileHover={{ y: -1 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => toggleCity(c)}
                        style={{
                          padding: '6px 14px', borderRadius: 100, cursor: 'pointer',
                          fontSize: 12, fontWeight: 500, fontFamily: 'var(--tandm-font-sans)',
                          border: active ? 'none' : '1.5px solid #D9E2E6',
                          background: active ? '#0B1220' : 'rgba(255,255,255,0.8)',
                          color: active ? '#fff' : '#475569',
                          boxShadow: active ? '0 2px 8px rgba(26,18,8,0.2)' : 'none',
                          transition: 'all 0.15s',
                        }}
                      >
                        {c}
                      </motion.button>
                    );
                  })}
                </div>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', display: 'flex', pointerEvents: 'none' }}><Ic.pin /></span>
                  <input
                    name="city"
                    value={cities.join(', ')}
                    onChange={e => setFormData({ ...formData, city: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                    style={{ width: '100%', padding: '11px 14px 11px 34px', border: '1.5px solid #D9E2E6', borderRadius: 11, fontSize: 13, fontFamily: 'var(--tandm-font-sans)', color: '#0B1220', background: 'rgba(250,250,248,0.9)', transition: 'all 0.2s' }}
                    placeholder="Or type cities separated by commas..."
                  />
                </div>
              </div>

              {/* International toggle */}
              <motion.div
                animate={{ background: isInternational ? 'linear-gradient(135deg,rgba(255,248,244,1),rgba(255,240,232,1))' : 'rgba(250,250,248,0.8)', borderColor: isInternational ? '#F0D5C4' : '#D9E2E6' }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', border: '1.5px solid', borderRadius: 14, transition: 'border-color 0.25s' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: isInternational ? '#FFF0E8' : '#F0EBE5', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}>
                    <Ic.globe />
                  </div>
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 600, color: '#0B1220', margin: '0 0 2px' }}>International student</p>
                    <p style={{ fontSize: 11, color: '#94A3B8', margin: 0 }}>
                      {isInternational ? "We'll prioritise roles open to internationals" : 'Toggle on if you need visa sponsorship'}
                    </p>
                  </div>
                </div>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsInternational(v => !v)}
                  style={{ width: 44, height: 24, borderRadius: 100, border: 'none', cursor: 'pointer', position: 'relative', flexShrink: 0, background: isInternational ? '#4E6C7E' : '#C5D0D6', transition: 'background 0.2s' }}
                >
                  <motion.div
                    animate={{ left: isInternational ? 23 : 3 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                    style={{ position: 'absolute', top: 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.18)' }}
                  />
                </motion.button>
              </motion.div>

              {/* Nationality */}
              <AnimatePresence>
                {isInternational && (
                  <motion.div
                    initial={{ opacity: 0, height: 0, marginTop: 0 }}
                    animate={{ opacity: 1, height: 'auto', marginTop: 12 }}
                    exit={{ opacity: 0, height: 0, marginTop: 0 }}
                    style={{ overflow: 'hidden' }}
                  >
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 }}>Nationality / country of origin</label>
                    <input
                      name="nationality"
                      value={formData.nationality}
                      onChange={handleChange}
                      style={{ width: '100%', padding: '11px 14px', border: '1.5px solid #D9E2E6', borderRadius: 11, fontSize: 13, fontFamily: 'var(--tandm-font-sans)', color: '#0B1220', background: 'rgba(250,250,248,0.9)', transition: 'all 0.2s' }}
                      placeholder="e.g. Indian, Nigerian, German..."
                    />
                    <p style={{ fontSize: 11, color: '#94A3B8', margin: '7px 0 0', lineHeight: 1.55 }}>
                      Used to improve match scores — international students get boosted scores on roles welcoming all applicants.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </Card>

            {/* Personal Details */}
            <Card delay={0.16}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                <div style={{ width: 32, height: 32, borderRadius: 9, background: '#FFF0E8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="16" height="16" fill="none" stroke="#4E6C7E" viewBox="0 0 24 24" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                </div>
                <div>
                  <h2 style={{ fontSize: 14, fontWeight: 700, color: '#0B1220', margin: 0, letterSpacing: '-0.02em' }}>Personal details</h2>
                  <p style={{ fontSize: 11, color: '#94A3B8', margin: 0 }}>Used in your cover letters and profile</p>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11 }}>
                  {[
                    { name: 'first_name', label: 'First name', placeholder: 'Jane' },
                    { name: 'last_name',  label: 'Last name',  placeholder: 'Smith' },
                  ].map(f => (
                    <div key={f.name}>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 }}>{f.label}</label>
                      <input name={f.name} value={formData[f.name]} onChange={handleChange} placeholder={f.placeholder}
                        style={{ width: '100%', padding: '11px 14px', border: '1.5px solid #D9E2E6', borderRadius: 11, fontSize: 13, fontFamily: 'var(--tandm-font-sans)', color: '#0B1220', background: 'rgba(250,250,248,0.9)', transition: 'all 0.2s' }} />
                    </div>
                  ))}
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 }}>Email</label>
                  <input value={user?.email || ''} disabled style={{ width: '100%', padding: '11px 14px', border: '1.5px solid #D9E2E6', borderRadius: 11, fontSize: 13, fontFamily: 'var(--tandm-font-sans)', color: '#94A3B8', background: '#EEF2F4', cursor: 'not-allowed' }} />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 }}>University</label>
                  <input name="university" type="text" value={formData.university} onChange={handleChange} placeholder="LSE"
                    style={{ width: '100%', padding: '11px 14px', border: '1.5px solid #D9E2E6', borderRadius: 11, fontSize: 13, fontFamily: 'var(--tandm-font-sans)', color: '#0B1220', background: 'rgba(250,250,248,0.9)', transition: 'all 0.2s' }} />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 }}>Degree</label>
                  <input name="degree" value={formData.degree} onChange={handleChange} placeholder="BSc Finance"
                    style={{ width: '100%', padding: '11px 14px', border: '1.5px solid #D9E2E6', borderRadius: 11, fontSize: 13, fontFamily: 'var(--tandm-font-sans)', color: '#0B1220', background: 'rgba(250,250,248,0.9)', transition: 'all 0.2s' }} />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 11 }}>
                  {[
                    { name: 'graduation_year',  label: 'Grad year',  placeholder: '2026', type: 'number' },
                    { name: 'graduation_month', label: 'Grad month', placeholder: '6',    type: 'number' },
                    { name: 'years_experience', label: 'Years exp.',  placeholder: '1',   type: 'number' },
                  ].map(f => (
                    <div key={f.name}>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 }}>{f.label}</label>
                      <input name={f.name} type={f.type} value={formData[f.name]} onChange={handleChange} placeholder={f.placeholder}
                        style={{ width: '100%', padding: '11px 14px', border: '1.5px solid #D9E2E6', borderRadius: 11, fontSize: 13, fontFamily: 'var(--tandm-font-sans)', color: '#0B1220', background: 'rgba(250,250,248,0.9)', transition: 'all 0.2s' }} />
                    </div>
                  ))}
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 }}>LinkedIn URL</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', display: 'flex', pointerEvents: 'none' }}><Ic.linkedin /></span>
                    <input name="linkedin_url" value={formData.linkedin_url} onChange={handleChange} placeholder="https://linkedin.com/in/yourname"
                      style={{ width: '100%', padding: '11px 14px 11px 34px', border: '1.5px solid #D9E2E6', borderRadius: 11, fontSize: 13, fontFamily: 'var(--tandm-font-sans)', color: '#0B1220', background: 'rgba(250,250,248,0.9)', transition: 'all 0.2s' }} />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11 }}>
                  {[
                    { name: 'phone', label: 'Phone number', placeholder: '+44 7700 900000', type: 'tel' },
                    { name: 'current_location', label: 'Current location', placeholder: 'London, United Kingdom', type: 'text' },
                  ].map(f => (
                    <div key={f.name}>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 }}>{f.label}</label>
                      <input name={f.name} type={f.type} value={formData[f.name]} onChange={handleChange} placeholder={f.placeholder}
                        style={{ width: '100%', padding: '11px 14px', border: '1.5px solid #D9E2E6', borderRadius: 11, fontSize: 13, fontFamily: 'var(--tandm-font-sans)', color: '#0B1220', background: 'rgba(250,250,248,0.9)', transition: 'all 0.2s' }} />
                    </div>
                  ))}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11 }}>
                  {[
                    { name: 'country', label: 'Country', placeholder: 'United Kingdom', type: 'text' },
                  ].map(f => (
                    <div key={f.name}>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 }}>{f.label}</label>
                      <input name={f.name} type={f.type} value={formData[f.name]} onChange={handleChange} placeholder={f.placeholder}
                        style={{ width: '100%', padding: '11px 14px', border: '1.5px solid #D9E2E6', borderRadius: 11, fontSize: 13, fontFamily: 'var(--tandm-font-sans)', color: '#0B1220', background: 'rgba(250,250,248,0.9)', transition: 'all 0.2s' }} />
                    </div>
                  ))}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 }}>Work authorization</label>
                    <select
                      name="work_authorization"
                      value={formData.work_authorization}
                      onChange={handleChange}
                      style={{ width: '100%', padding: '11px 14px', border: '1.5px solid #D9E2E6', borderRadius: 11, fontSize: 13, fontFamily: 'var(--tandm-font-sans)', color: '#0B1220', background: 'rgba(250,250,248,0.9)', transition: 'all 0.2s' }}
                    >
                      <option value="">Select status</option>
                      <option value="Authorized to work without sponsorship">Authorized to work without sponsorship</option>
                      <option value="Requires visa sponsorship">Requires visa sponsorship</option>
                      <option value="Need employer clarification">Need employer clarification</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 }}>Sponsorship needed</label>
                    <select
                      name="requires_sponsorship"
                      value={String(Boolean(formData.requires_sponsorship))}
                      onChange={e => setFormData({ ...formData, requires_sponsorship: e.target.value === 'true' })}
                      style={{ width: '100%', padding: '11px 14px', border: '1.5px solid #D9E2E6', borderRadius: 11, fontSize: 13, fontFamily: 'var(--tandm-font-sans)', color: '#0B1220', background: 'rgba(250,250,248,0.9)', transition: 'all 0.2s' }}
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 }}>Gender</label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      style={{ width: '100%', padding: '11px 14px', border: '1.5px solid #D9E2E6', borderRadius: 11, fontSize: 13, fontFamily: 'var(--tandm-font-sans)', color: '#0B1220', background: 'rgba(250,250,248,0.9)', transition: 'all 0.2s' }}
                    >
                      <option value="">Prefer not to say</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Non-binary / gender non-conforming">Non-binary / gender non-conforming</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div />
                </div>

                <motion.button
                  whileHover={{ scale: 1.02, boxShadow: '0 8px 24px rgba(78,108,126,0.3)' }}
                  whileTap={{ scale: 0.97 }}
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    width: '100%', padding: '13px', marginTop: 4,
                    background: saving ? '#D4956A' : 'var(--tandm-gradient-cta)',
                    color: '#fff', border: 'none', borderRadius: 12,
                    fontSize: 14, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
                    fontFamily: 'var(--tandm-font-sans)',
                    boxShadow: '0 4px 16px rgba(78,108,126,0.25)',
                  }}
                >
                  {saving
                    ? <><div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />Saving...</>
                    : <><Ic.save />Save changes</>
                  }
                </motion.button>
              </div>
            </Card>
          </div>

          {/* ── SIDEBAR ──────────────────────────────────── */}
          <div className="pf-sidebar" style={{ display: 'flex', flexDirection: 'column', gap: 12, position: 'sticky', top: 'var(--tandm-nav-sticky-top)' }}>

            {/* Profile card */}
            <FadeIn delay={0.05}>
              <div style={{ background: '#0B1220', borderRadius: 18, padding: 20, position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 80% 80% at 100% 0%,rgba(78,108,126,0.18),transparent)', pointerEvents: 'none' }} />
                <motion.div
                  animate={{ scale: [1, 1.1, 1], opacity: [0.08, 0.14, 0.08] }}
                  transition={{ duration: 8, repeat: Infinity }}
                  style={{ position: 'absolute', width: 120, height: 120, borderRadius: '50%', background: 'radial-gradient(circle,#6366F1 0%,transparent 70%)', bottom: -30, left: -20, pointerEvents: 'none' }}
                />
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--tandm-gradient-cta)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontWeight: 800, color: '#fff', marginBottom: 11 }}>
                    {formData.first_name?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <p style={{ fontSize: 14, fontWeight: 700, color: '#fff', margin: '0 0 2px', letterSpacing: '-0.02em' }}>
                    {formData.first_name || 'Your name'} {formData.last_name}
                  </p>
                  <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', margin: '0 0 10px' }}>{user?.email}</p>
                  {formData.university && <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', margin: '0 0 2px' }}>{formData.university}</p>}
                  {formData.degree && <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', margin: '0 0 2px' }}>{formData.degree}</p>}
                  {formData.graduation_year && <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', margin: '0 0 8px' }}>Class of {formData.graduation_year}</p>}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {cities.slice(0, 2).map(c => (
                      <span key={c} style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.07)', padding: '3px 9px', borderRadius: 100 }}> {c}</span>
                    ))}
                    {isInternational && <span style={{ fontSize: 10, color: '#F59E0B', background: 'rgba(245,158,11,0.12)', padding: '3px 9px', borderRadius: 100 }}> International</span>}
                  </div>
                  {targetSectors.length > 0 && (
                    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {targetSectors.slice(0, 2).map(id => (
                        <span key={id} style={{ fontSize: 10, fontWeight: 500, color: 'rgba(255,255,255,0.45)', background: 'rgba(255,255,255,0.07)', padding: '2px 8px', borderRadius: 100 }}>
                          {SECTORS.find(s => s.id === id)?.label}
                        </span>
                      ))}
                      {targetSectors.length > 2 && <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>+{targetSectors.length - 2} more</span>}
                    </div>
                  )}
                </div>
              </div>
            </FadeIn>

            {/* Checklist */}
            <FadeIn delay={0.1}>
              <div style={{ background: 'rgba(255,255,255,0.78)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.95)', borderRadius: 16, padding: 17, boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <p style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.07em', margin: 0 }}>Checklist</p>
                  <span style={{ fontSize: 11, fontWeight: 700, color: pct === 100 ? '#065F46' : '#4E6C7E' }}>{pct}%</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {checkItems.map((item, i) => (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                      style={{ display: 'flex', alignItems: 'center', gap: 9 }}
                    >
                      <div style={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: item.done ? '#ECFDF5' : '#EEF2F4', border: `1px solid ${item.done ? '#A7F3D0' : '#D9E2E6'}`, transition: 'all 0.3s' }}>
                        {item.done
                          ? <Ic.check />
                          : <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#94A3B8' }} />
                        }
                      </div>
                      <span style={{ fontSize: 12, color: item.done ? '#3D2E24' : '#94A3B8', fontWeight: item.done ? 500 : 400 }}>{item.label}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </FadeIn>

            {/* Why it matters */}
            <FadeIn delay={0.15}>
              <div style={{ background: 'linear-gradient(135deg,#E8F1F3,#FFF0E8)', border: '1px solid #F0D5C4', borderRadius: 14, padding: 15 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                  <Ic.info />
                  <p style={{ fontSize: 10, fontWeight: 700, color: '#4E6C7E', textTransform: 'uppercase', letterSpacing: '0.07em', margin: 0 }}>Why it matters</p>
                </div>
                <p style={{ fontSize: 12, color: '#475569', margin: 0, lineHeight: 1.65 }}>Your sectors and location feed directly into match scoring. International students get boosted scores on roles that explicitly welcome all applicants.</p>
              </div>
            </FadeIn>

          </div>
        </div>
      </div>

      <Toast toast={toast} />
    </div>
  );
}
